import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { COUNTRY_FLAGS } from "@/data/compass";
import { useAuth } from "@/context/AuthContext";
import { MapPin } from "@phosphor-icons/react";

/**
 * LocationBadge — canonical way to display a user's location across Ruta.
 *
 * Priority order:
 *   1. Explicit `country` + `city` props (used when a Ruta or record carries
 *      its own location, independent from the user's home base).
 *   2. Current user's saved settings (loaded once via /api/settings and
 *      broadcast across the app via a lightweight in-memory cache).
 *
 * Variants:
 *   - "chip"   → compact bordered pill (default)
 *   - "inline" → plain text with flag, for headers/subheaders
 *   - "tag"    → uppercase mono label style for card headers
 */

// Tiny in-memory cache so we do not refetch /settings on every render.
let _cachedLocation = null;
const _listeners = new Set();
export function updateLocationCache(next) {
  _cachedLocation = next;
  _listeners.forEach((cb) => cb(next));
}

function useResolvedLocation(propCountry, propCity) {
  const { user, token } = useAuth();
  const [state, setState] = useState(_cachedLocation);

  useEffect(() => {
    if (propCountry || propCity) return; // props win
    if (!token) return;
    if (_cachedLocation) { setState(_cachedLocation); return; }
    let cancelled = false;
    api.get("/settings").then((r) => {
      const next = { country: r.data.country || null, city: r.data.city || null };
      if (cancelled) return;
      updateLocationCache(next);
    }).catch(() => {});
    const listener = (v) => !cancelled && setState(v);
    _listeners.add(listener);
    return () => { cancelled = true; _listeners.delete(listener); };
  }, [propCountry, propCity, token, user?.id]);

  if (propCountry || propCity) return { country: propCountry, city: propCity };
  return state || { country: null, city: null };
}

export default function LocationBadge({
  country: propCountry,
  city: propCity,
  variant = "chip",
  hideIfEmpty = true,
  className = "",
  testid = "location-badge",
}) {
  const { country, city } = useResolvedLocation(propCountry, propCity);
  const flag = country ? COUNTRY_FLAGS[country] || "" : "";
  const hasSomething = country || city;

  if (!hasSomething && hideIfEmpty) return null;

  const label = [city, country].filter(Boolean).join(", ") || "Sin ubicación";

  if (variant === "inline") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-sm text-muted-foreground ${className}`}
        data-testid={testid}
      >
        {flag && <span aria-hidden className="text-base leading-none">{flag}</span>}
        <span className="truncate">{label}</span>
      </span>
    );
  }

  if (variant === "tag") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 label-mono text-[0.65rem] text-muted-foreground ${className}`}
        data-testid={testid}
      >
        {flag && <span aria-hidden className="text-sm leading-none">{flag}</span>}
        <span className="truncate">{label}</span>
      </span>
    );
  }

  // default: "chip"
  return (
    <span
      className={`inline-flex items-center gap-1.5 border-2 border-foreground bg-surface px-2.5 py-1 text-xs font-semibold shadow-[2px_2px_0_0_hsl(var(--foreground))] ${className}`}
      data-testid={testid}
    >
      {flag ? (
        <span aria-hidden className="text-base leading-none">{flag}</span>
      ) : (
        <MapPin size={12} weight="bold" />
      )}
      <span className="truncate max-w-[220px]">{label}</span>
    </span>
  );
}
